import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { User } from '@models/user/user.model';
import { first, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

import { AuthService } from './auth.service';
import { SetupFlow } from '@models/user/setupFlow.model';
import { CPRNumber } from '@app/dialogs/profileRelatedDialogs/completeProfile/UI/cpr-step/cpr-step.component';
import { TierData } from '@app/interfaces/user/TierData.type.interface';

@Injectable()
export class UserService {
  baseHref = `${environment.apiUrl}/users`;
  userSettingsHref = `${environment.apiUrl}/users-settings`;

  constructor(
    private authService: AuthService,
    private http: HttpClient,
  ) {}

  adminChangeTier(data: any): Observable<any> {
    const url = `${this.baseHref}/admin-change-tier`;
    return this.http.post<any>(url, { tier: data });
  }

  changePassword(data: any) {
    const url = `${this.baseHref}/change-password`;
    return this.http.post(url, data);
  }

  contactUsLanding(data: any): Observable<any> {
    const url = `${this.baseHref}/contact-us-landing`;
    return this.http.post<any>(url, data);
  }

  completeProfileViewed(bool: boolean): Observable<any> {
    const url = `${this.baseHref}/viewed-comp-profile`;
    return this.http.post<any>(url, bool);
  }

  createUser(userData: any): Observable<any> {
    const url = `${this.baseHref}`;
    return this.http.post<any>(url, userData);
  }

  deleteProfile(): Observable<any> {
    const id = this.authService.getUserIdFromAuthToken();
    const url = `${this.baseHref}/${id}`;
    return this.http.delete<any>(url);
  }

  editUserProfile(userData: any): Observable<User> {
    const id = this.authService.getUserIdFromAuthToken();
    const url = `${this.baseHref}/${id}`;
    return this.http.put<User>(url, userData);
  }

  getUserProfile(): Observable<User> {
    const url = `${this.baseHref}/full-user`;
    return this.http.get<User>(url);
  }

  getUserNameById(userId: number): Observable<User> {
    const id = userId || this.authService.getUserIdFromAuthToken();
    const url = `${this.baseHref}/user-name-and-photo/${id}`;
    return this.http.get<User>(url);
  }

  getAllUserProfile(userId: number): Observable<User> {
    const id = userId || this.authService.getUserIdFromAuthToken();
    const url = `${this.baseHref}/profile/${id}`;
    return this.http.get<User>(url);
  }

  getRawUser(userId: number): Observable<User> {
    const id = userId || this.authService.getUserIdFromAuthToken();
    const url = `${this.baseHref}/raw/${id}`;
    return this.http.get<User>(url);
  }

  checkUserExists(email: string): Observable<boolean> {
    const url = `${this.baseHref}/exists`;
    return this.http.post<any>(url, { email }).pipe(
      map((res) => res.exists),
      first(),
    );
  }

  // Cached
  getAllUserProfileCached(userId: number): Observable<User> {
    const id = userId || this.authService.getUserIdFromAuthToken();
    const url = `${this.baseHref}/profile/cached/${id}`;
    return this.http.get<User>(url);
  }

  forgotPassword(data: { email: string }) {
    const url = `${this.baseHref}/forgot-password`;
    return this.http.post(url, data);
  }

  resetPassword(data: any): Observable<any> {
    const url = `${this.baseHref}/reset-password`;
    return this.http.post<any>(url, data);
  }

  requestVisitedPPUserToTask(data: {
    taskId: number;
    currentUserId: number;
    visitedUserId: number;
  }): Observable<any> {
    const url = `${this.baseHref}/request-visited-pp-user-to-task`;
    return this.http.post<any>(url, data);
  }

  updateHeaderImage(headerImgData: { media: any[] }): Observable<any> {
    const url = `${this.baseHref}/header-image`;
    return this.http.post<any>(url, headerImgData);
  }

  findRaw(userId: number) {
    const url = `${this.baseHref}/raw/${userId}`;
    return this.http.get<any>(url);
  }

  getTierData(): Observable<any> {
    const url = `${this.baseHref}/config-tier-data`;
    return this.http.get<any>(url);
  }

  getUserTierData(): Observable<TierData> {
    const url = `${this.baseHref}/tier-data`;
    return this.http.get<TierData>(url);
  }

  sendSmsVerificationCode(phoneNumber: string) {
    const url = `${this.baseHref}/sendVerificationCode`;
    return this.http.post<string>(url, { number: `+45${phoneNumber}` });
  }

  requestAppLinkBySms(): Observable<any> {
    const url = `${this.baseHref}/requestAppLinkBySms`;
    return this.http.get<any>(url);
  }

  updateFBandPicture(imageUrl: string): Observable<boolean> {
    const url = `${this.baseHref}/verifyFBandMaybeAddPicture`;
    return this.http.post<boolean>(url, { imageUrl });
  }

  verifyReceivedSmsVerificationCode(phoneNumber: string, smsCode: string) {
    const url = `${this.baseHref}/verifySentVerificationCode`;
    return this.http.post<string>(url, {
      number: `+45${phoneNumber}`,
      smsCode: `${smsCode}`,
    });
  }

  setSetupDone(data: SetupFlow): Observable<User> {
    const url = `${this.baseHref}/setup-done`;
    return this.http.post<User>(url, data);
  }

  getUserHasCPR(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const url = `${this.baseHref}/has-cpr`;
      this.http.get<any>(url).subscribe({
        next: (value) => {
          if (value.hasCPR) {
            resolve(true);
          } else {
            resolve(false);
          }
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  }

  getDOBFromUserCPR(): Observable<any> {
    const url = `${this.baseHref}/get-cpr`;
    return this.http.get<any>(url);
  }

  saveUserCPR(cpr: CPRNumber) {
    const url = `${this.baseHref}/save-cpr`;
    return this.http.post<any>(url, {
      cprNumber: cpr.cpr,
      countryCode: cpr.countryCode,
    });
  }

  getUseTemplatesByDefault(): Observable<{ enabled: boolean }> {
    const url = `${this.userSettingsHref}/use-templates-by-default`;
    return this.http.get<{ enabled: boolean }>(url);
  }

  setUseTemplatesByDefault(enabled: boolean): Observable<any> {
    const url = `${this.userSettingsHref}/use-templates-by-default`;
    return this.http.put<any>(url, { enabled });
  }

  saveAdsClick(data: AdsClickEntry | AdsClickEntry[]) {
    const url = `${this.baseHref}/save-ads-click`;
    return this.http.post<any>(url, data);
  }

  getMitIdAuthorizationUrl(): Observable<any> {
    const url = `${this.baseHref}/mit-id/authorization-url`;
    return this.http.get<any>(url);
  }

  exchangeCodeForToken(callbackUrl: string): Observable<any> {
    const url = `${this.baseHref}/mit-id/callback`;
    return this.http.post<any>(url, { callbackUrl });
  }
}

type AdsClickEntry = {
  adsClickType: string;
  adsClickValue: string;
  clickTimeStamp: string;
};
